import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { useRefreshGuard } from '../refreshGuards'
import { useLiveRoom } from '../LiveRoom'
import { ArrowDown, ArrowDownLeft, ArrowUp, ArrowUpRight, GripVertical, Link2, X } from 'lucide-react'
import { addConnection, referenceTargets, reorderConnection } from '../connectedBible'
import { ConnectionPicker } from './ConnectionPicker'
import type { StoryProject } from '../types'

export function BibleConnections({
  project,
  sourceId,
  onChange,
  onOpen,
}: {
  project: StoryProject
  sourceId: string
  onChange: (p: StoryProject, key?: string) => void
  onOpen: (id: string) => void
}) {
  const room = useLiveRoom()
  const [target, setTarget] = useState('')
  const [relationship, setRelationship] = useState('related to')
  const [lastRelationship, setLastRelationship] = useState(relationship)
  const [query, setQuery] = useState(''), [notice, setNotice] = useState('')
  const [drag, setDrag] = useState<{ id: string; over: string | null } | null>(null)
  const list = useRef<HTMLDivElement>(null)
  const gesture = useRef<{ id: string; startX: number; startY: number; x: number; y: number; active: boolean } | null>(null)
  const latest = useRef({ project, sourceId, onChange })
  latest.current = { project, sourceId, onChange }
  useRefreshGuard(!!target || !!query.trim() || relationship !== lastRelationship || !!drag, 'Finish adding or moving your connection before refreshing.')
  const targets = referenceTargets(project)
  const links = (project.bible?.links || []).filter(
    (l) => l.sourceId === sourceId || l.targetId === sourceId,
  )
  const targetExists = targets.some(item => item.id === target && item.id !== sourceId)
  const hit = (x: number, y: number) => {
    const row = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-connection-id]')
    return row && list.current?.contains(row) ? row.dataset.connectionId! : null
  }
  const move = (id: string, destination: string) => {
    const current = latest.current
    const next = reorderConnection(current.project, current.sourceId, id, destination)
    if (next === current.project) return
    latest.current = { ...current, project: next }
    // A move must undo independently of a connection just added or text typed.
    const live = room?.document.current
    live?.undo.stopCapturing()
    current.onChange(next, 'reorder-link:' + id)
    live?.undo.stopCapturing()
    const position = next.bible!.links!.filter(link => link.sourceId === current.sourceId || link.targetId === current.sourceId).findIndex(link => link.id === id)
    setNotice('Connection moved to position ' + (position + 1) + '.')
  }
  const startDrag = (event: ReactPointerEvent<HTMLButtonElement>, id: string) => {
    if (!event.isPrimary || event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    gesture.current = { id, startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY, active: false }
  }
  const duringDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const current = gesture.current
    if (!current) return
    current.x = event.clientX; current.y = event.clientY
    if (!current.active && Math.hypot(current.x - current.startX, current.y - current.startY) < 6) return
    event.preventDefault(); current.active = true
    setDrag({ id: current.id, over: hit(current.x, current.y) })
  }
  const cancelDrag = () => { gesture.current = null; setDrag(null) }
  const endDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const current = gesture.current, destination = hit(event.clientX, event.clientY)
    cancelDrag()
    if (current?.active && destination) { event.preventDefault(); move(current.id, destination) }
  }
  useEffect(() => {
    if (!drag) return
    let frame = 0
    const scroll = () => {
      const current = gesture.current
      if (!current) return
      let container = list.current?.parentElement || null
      while (container && !(container.scrollHeight > container.clientHeight && /auto|scroll/.test(getComputedStyle(container).overflowY))) container = container.parentElement
      if (container) {
        const box = container.getBoundingClientRect(), rows = list.current!.getBoundingClientRect()
        if (current.x >= box.left && current.x <= box.right) {
          const speed = current.y < box.top + 40 && rows.top < box.top ? -12 : current.y > box.bottom - 40 && rows.bottom > box.bottom ? 12 : 0
          if (speed) {
            container.scrollTop += speed
            const over = hit(current.x, current.y)
            setDrag(old => old && old.over !== over ? { ...old, over } : old)
          }
        }
      }
      frame = requestAnimationFrame(scroll)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); cancelDrag() }
    }
    frame = requestAnimationFrame(scroll)
    window.addEventListener('keydown', escape, true)
    return () => { cancelAnimationFrame(frame); window.removeEventListener('keydown', escape, true) }
  }, [drag?.id])
  return (
    <section className="bible-connections" data-tour="connections">
      <h3>
        <Link2 size={16} /> Connections <span>{links.length}</span>
      </h3>
      <p>
        Connect people, places, and scenes. Connections appear at both ends.
        {links.length > 1 && ' Drag the handles or use the arrows to change their order.'}
      </p>
      <div className="connection-list" ref={list} role="list" aria-label="Connections">
        {links.map((link, index) => {
          const outgoing = link.sourceId === sourceId
          const other = targets.find(
            (t) => t.id === (outgoing ? link.targetId : link.sourceId),
          )
          return (
            <div key={link.id} role="listitem" data-connection-id={link.id}
              className={'connection-row' + (drag?.id === link.id ? ' is-dragging' : '') + (drag?.over === link.id && drag.id !== link.id ? ' is-drop-target' : '')}>
              {links.length > 1 && <button type="button" className="icon-button connection-grip" aria-label={'Drag connection to ' + (other?.title || 'Removed entry')}
                title="Drag to reorder; use the arrow buttons with a keyboard" onPointerDown={event => startDrag(event, link.id)} onPointerMove={duringDrag}
                onPointerUp={endDrag} onPointerCancel={cancelDrag} onLostPointerCapture={cancelDrag}><GripVertical size={16} /></button>}
              <span
                title={
                  outgoing ? 'Links from this entry' : 'Links to this entry'
                }
              >
                {outgoing ? (
                  <ArrowUpRight size={14} />
                ) : (
                  <ArrowDownLeft size={14} />
                )}
              </span>
              <button
                type="button"
                onClick={() => onOpen(other!.id)}
                disabled={!other}
              >
                {outgoing ? (
                  <>
                    <small>{link.relationship}</small>
                    {other?.title || 'Removed entry'}
                  </>
                ) : (
                  <>
                    {other?.title || 'Removed entry'}
                    <small>{link.relationship} this entry</small>
                  </>
                )}
              </button>
              {links.length > 1 && <div className="connection-order-buttons">
                <button type="button" className="icon-button" aria-label={'Move connection to ' + (other?.title || 'Removed entry') + ' up'}
                  disabled={index === 0} onClick={() => move(link.id, links[index - 1].id)}><ArrowUp size={14} /></button>
                <button type="button" className="icon-button" aria-label={'Move connection to ' + (other?.title || 'Removed entry') + ' down'}
                  disabled={index === links.length - 1} onClick={() => move(link.id, links[index + 1].id)}><ArrowDown size={14} /></button>
              </div>}
              <button
                type="button"
                className="icon-button"
                aria-label={
                  'Remove connection ' + link.relationship + ' ' + other?.title
                }
                onClick={() =>
                  onChange(
                    {
                      ...project,
                      bible: {
                        ...project.bible!,
                        links: project.bible!.links!.filter(
                          (l) => l.id !== link.id,
                        ),
                      },
                    },
                    'remove-link',
                  )
                }
              >
                <X size={13} />
              </button>
            </div>
          )
        })}
      </div>
      <span className="sr-only" role="status">{notice}</span>
      <form
        className="connection-form"
        onSubmit={(event) => {
          event.preventDefault()
          if (targetExists) {
            const next = addConnection(project, sourceId, target, relationship.trim() || 'related to')
            if (next !== project) onChange(next, 'add-link')
            setNotice(next === project ? 'That connection already exists.' : 'Connection added.')
            setTarget('')
            setLastRelationship(relationship)
          }
        }}
      >
        <label>
          Relationship
          <input
            value={relationship}
            maxLength={100}
            onChange={(event) => setRelationship(event.target.value)}
            placeholder="lives in, knows, takes place in…"
          />
        </label>
        <ConnectionPicker project={project} sourceId={sourceId} value={target} onChange={setTarget} onQueryChange={setQuery} />
        <button type="submit" className="button" disabled={!targetExists}>
          <Link2 size={13} /> Connect
        </button>
      </form>
    </section>
  )
}
